import { PhotographIcon } from '@heroicons/react/outline';
import React, { ChangeEvent, useRef, useState } from 'react';

const SANITY_PROJECT_ID = 'urerbr0z';
const SANITY_DATASET = 'production';
const SANITY_API_VERSION = '2024-01-01';

type Props = {
  label: string;
  name: string;
  value?: string;
  onChange: (url: string) => void;
  className?: string;
};

const SanityImageUploader: React.FC<Props> = ({
  label,
  name,
  value,
  onChange,
  className = '',
}) => {
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onChangeFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const token = process.env.REACT_APP_SANITY_TOKEN;
    if (!token) {
      setError('REACT_APP_SANITY_TOKEN is not set.');
      return;
    }

    setError(null);
    setUploading(true);
    try {
      const response = await fetch(
        `https://${SANITY_PROJECT_ID}.api.sanity.io/v${SANITY_API_VERSION}/assets/images/${SANITY_DATASET}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': file.type || 'application/octet-stream',
          },
          body: file,
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Upload failed (${response.status}): ${text}`);
      }

      const data = await response.json();
      // Sanity returns { document: { _id, url, ... } }
      const url = data.document?.url ?? data.url;
      if (!url) throw new Error('No URL returned from Sanity.');
      onChange(url);
    } catch (err: any) {
      setError(err?.message || 'Upload failed. Check your Sanity token.');
      console.error('Sanity image upload failed:', err);
    } finally {
      setUploading(false);
      // Reset input so the same file can be re-selected if needed
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className={className}>
      <label
        htmlFor={name}
        className="block text-sm font-medium leading-5 text-gray-900 sm:mt-px sm:pt-2"
      >
        {label}
      </label>
      <div className="mt-1 flex items-center gap-4">
        {/* Thumbnail preview */}
        <span className="h-16 w-16 rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
          {value ? (
            <img
              className="object-cover w-full h-full"
              src={value}
              alt={label}
            />
          ) : (
            <PhotographIcon className="h-full w-full text-gray-300 p-2" />
          )}
        </span>

        <div className="flex flex-col gap-1.5">
          <div className="flex gap-2">
            <button
              type="button"
              disabled={uploading}
              className="bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              onClick={() => input.current?.click()}
            >
              {uploading ? 'Uploading…' : value ? 'Change' : 'Upload image'}
            </button>
            {value && (
              <button
                type="button"
                className="bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => onChange('')}
              >
                Remove
              </button>
            )}
          </div>
          {error && (
            <p className="text-xs text-red-600">{error}</p>
          )}
          {value && (
            <p className="text-xs text-gray-400 truncate max-w-xs">{value}</p>
          )}
        </div>
      </div>
      <input
        ref={input}
        onChange={onChangeFile}
        accept="image/*"
        multiple={false}
        type="file"
        hidden
      />
    </div>
  );
};

export default SanityImageUploader;
